import moment from 'moment'
import { TimeRange, timeRangeToMinutes } from 'types/search/filters'

/**
 * Calculate effective date range based on time range preset or custom dates
 *
 * For preset time ranges (10m, 1h, etc):
 * - Calculates startDate = now() - interval
 * - Calculates endDate = now()
 * - Both dates are exact ISO timestamps
 *
 * For CUSTOM time range:
 * - Returns the provided custom dates as-is
 *
 * This ensures that:
 * 1. Preset ranges are always fresh (calculated at fetch time)
 * 2. Time intervals are exact for proper granularity calculation
 * 3. Chart bar width matches the API interval
 *
 * @param timeRange - Selected time range preset or CUSTOM
 * @param customStartDate - Start date for CUSTOM range (ISO string)
 * @param customEndDate - End date for CUSTOM range (ISO string)
 * @returns Object with startDate and endDate as ISO strings
 */
export const calculateEffectiveDateRange = (
  timeRange: TimeRange,
  customStartDate: string | null,
  customEndDate: string | null,
): { startDate: string; endDate: string } => {
  // For CUSTOM range, use the provided dates
  if (timeRange === TimeRange.CUSTOM) {
    // If custom dates are not provided, fall back to a default preset (last 10 min)
    if (!customStartDate) {
      const now = moment()
      const startDate = moment(now).subtract(10, 'minutes').toISOString()
      const endDate = now.toISOString()
      return { startDate, endDate }
    }

    // If no end date provided for custom range, use current time
    const endDate = customEndDate || moment().toISOString()

    return {
      startDate: customStartDate,
      endDate,
    }
  }

  // For preset ranges, calculate from current time
  const minutes = timeRangeToMinutes(timeRange)
  const now = moment()
  const startDate = moment(now).subtract(minutes, 'minutes').toISOString()

  return {
    startDate,
    endDate: now.toISOString(),
  }
}
